import { ref, computed } from "vue";

/**
 * Translation, hand-rolled.
 *
 * vue-i18n is the obvious dependency and it is deliberately not used. What this
 * codebase needs is a lookup, an interpolation and a reactive locale — about
 * sixty lines — against a library that brings a compiler, a message-format
 * parser and a bundle cost, on an app whose whole point is loading fast on a
 * phone. The same call was made for the QR code, the donut chart and the icons.
 *
 * What it deliberately does NOT do:
 *
 *   Pluralisation rules. Chinese and Vietnamese have no plural form, and the
 *   English strings here are written to avoid needing one ("1 / 3 left" rather
 *   than "one withdrawal left"). A rule engine for a case that does not arise
 *   is cost without a benefit.
 *
 *   Lazy-loaded locale chunks. Three languages of UI strings is a few KB; a
 *   network round trip to change language would be slower than shipping all
 *   three, and would flash untranslated text while it landed.
 */

const FALLBACK = "en";

const bundles = ref({});
const current = ref(FALLBACK);

/** The locale actually in use, which is not always the one that was asked for. */
export const locale = computed(() => current.value);

/**
 * Resolves a dotted key against the active bundle, then the fallback.
 *
 * A missing key returns the KEY rather than an empty string. A blank label
 * looks like a rendering bug and gets reported as one; `money.tabs.deposit` on
 * screen says exactly which string is missing and in which file to add it.
 */
function resolve(bundle, key) {
  let node = bundle;

  for (const part of key.split(".")) {
    if (node === null || typeof node !== "object") return undefined;
    node = node[part];
  }

  return typeof node === "string" ? node : undefined;
}

/**
 * Translate. `t("money.held", { amount: "RM50" })` fills `{amount}`.
 *
 * Reads `current.value`, so any template calling this re-renders when the
 * language changes — no event bus, no key on the router view.
 */
export function t(key, params) {
  const active = bundles.value[current.value];
  const text = resolve(active, key)
    ?? resolve(bundles.value[FALLBACK], key)
    ?? key;

  if (!params) return text;

  return text.replace(/\{(\w+)\}/g, (whole, name) =>
    params[name] === undefined ? whole : String(params[name]));
}

/**
 * Switches language and remembers it.
 *
 * Unknown locales are ignored rather than accepted, because the stored value
 * comes from localStorage — a stale code from a locale that has since been
 * turned off would otherwise blank every string on the site.
 */
export function setLocale(next) {
  if (!bundles.value[next]) return false;

  current.value = next;
  document.documentElement.lang = next;

  try {
    localStorage.setItem("player.locale", next);
  } catch {
    // Private browsing denies writes. The language still applies for this
    // visit; only remembering it fails, which is not worth an error.
  }

  return true;
}

export function availableLocales() {
  return Object.keys(bundles.value);
}

/**
 * Installs the bundles and picks a starting language.
 *
 * Order is: what the player last chose, then what their browser asks for, then
 * English. The browser check is a prefix match so `zh-CN`, `zh-TW` and `zh-Hans`
 * all find `zh` — an exact match would send most Chinese-speaking visitors to
 * the English site.
 */
export function createI18n(messages) {
  bundles.value = messages;

  let stored = null;

  try {
    stored = localStorage.getItem("player.locale");
  } catch {
    // Same as above.
  }

  const wanted = [stored, ...(navigator.languages ?? [navigator.language ?? ""])]
    .filter(Boolean)
    .map((tag) => String(tag).toLowerCase());

  const found = wanted
    .map((tag) => Object.keys(messages).find((l) => tag === l || tag.startsWith(`${l}-`)))
    .find(Boolean);

  current.value = found ?? FALLBACK;
  document.documentElement.lang = current.value;

  return {
    install(app) {
      // Global so a template can say {{ $t("…") }} without an import in every
      // single-file component. `t` is still exported for use in script blocks.
      app.config.globalProperties.$t = t;
    },
  };
}
